import React, { Component } from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router';
import ADMIN_REGIONS from './Admin_Regions';
import withRouter from '../../withRouter'; // Import the HOC

import './Admin.css';
import ADMIN_ENEMY from './Admin_Enemy';
import ADMIN_RARITY from './Admin_Rarity';
import ADMIN_DAMAGATYPES from './Admin_DamageTypes';
import ADMIN_ENEMYTYPES from './Admin_EnemyTypes';
import ADMIN_ITEMTYPES from './Admin_ItemTypes';
import ADMIN_ITEMS from './Admin_Items';
import ADMIN_STATS from './Admin_Stats';
import ADMIN_LEVEL from './Admin_Level';
import ADMIN_SETTINGS from './Admin_Settings';
import ADMIN_WORLD_EDITOR from './WorldEditor/Admin_WorldEditor';
import ADMIN_TILES from './Admin_Tiles';
import ADMIN_CHANGELOG from './Admin_Changelog';
import ADMIN_OPERATIVES from './Admin_Operatives';
import ADMIN_CONTENT_PROMOTION from './Admin_ContentPromotion';
import ADMIN_STARTER_KITS from './Admin_StarterKits';
import ADMIN_PROFESSIONS from './Admin_Professions';
import ADMIN_BASE_CAMP_BUILDINGS from './Admin_BaseCampBuildings';
import ADMIN_WIKI from './Admin_Wiki';
import ADMIN_USERS from './Admin_Users';
import ADMIN_LOOT_CONTAINERS from './Admin_LootContainerTypes';
import ADMIN_CONTENT_OVERVIEW from './Admin_ContentOverview';
import { getChangelogStatus, getRegions } from './AdminAPI';
import ToastHost from '../common/ToastHost';
import {
    adminContentFilterSearch,
    clearAdminContentFilterSearch,
    parseAdminContentFilter,
    updateAdminContentFilterSearch,
} from './adminContentFilter';

const adminNavClassName = ({ isActive }) => `button-primary${isActive ? ' is-active' : ''}`;
const ADMIN_SIDEBAR_STORAGE_KEY = 'admin.sidebarCollapsed';
const ADMIN_NAV_GROUPS = [
    {
        label: 'Dashboard',
        items: [['Overview', 'overview']],
    },
    {
        label: 'World',
        items: [
            ['Regions', 'regions'],
            ['Tiles', 'tiles'],
            ['World Editor', 'world-editor'],
            ['Loot Containers', 'loot-containers'],
        ],
    },
    {
        label: 'Game Content',
        items: [
            ['Items', 'Items'],
            ['Item Types', 'ItemTypes'],
            ['Enemies', 'enemies'],
            ['Enemy Types', 'EnemyTypes'],
            ['Rarities', 'rarities'],
            ['Damage Types', 'DamageTypes'],
            ['Operatives', 'operatives'],
        ],
    },
    {
        label: 'Progression',
        items: [
            ['Professions', 'professions'],
            ['Camp Buildings', 'camp-buildings'],
            ['Starter Kits', 'starter-kits'],
            ['Stats', 'Stats'],
            ['Level', 'Level'],
        ],
    },
    {
        label: 'Administration',
        items: [
            ['General Settings', 'Settings'],
            ['Users', 'users'],
            ['Content Promotion', 'content-promotion'],
            ['Wiki', 'wiki'],
            ['Changelog', 'changelog'],
        ],
    },
];

function isNarrowAdminViewport() {
    return typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(max-width: 900px)').matches;
}

function readSidebarCollapsed() {
    if (typeof window === 'undefined') return false;

    try {
        return window.localStorage.getItem(ADMIN_SIDEBAR_STORAGE_KEY) === 'true';
    } catch (error) {
        console.warn('Could not load the Admin sidebar preference.', error);
        return false;
    }
}

function saveSidebarCollapsed(collapsed) {
    if (typeof window === 'undefined') return;

    try {
        window.localStorage.setItem(ADMIN_SIDEBAR_STORAGE_KEY, String(collapsed));
    } catch (error) {
        console.warn('Could not save the Admin sidebar preference.', error);
    }
}

export class AdminHome extends Component {
    constructor(props) {
        super(props);
        this.state = {
            token: "",
            changelogNeedsAttention: false,
            filterRegions: [],
            filterRegionsLoading: true,
            filterRegionsError: '',
            sidebarCollapsed: isNarrowAdminViewport() || readSidebarCollapsed(),
        };
    }

    componentDidMount() {
        this.refreshChangelogStatus();
        this.refreshFilterRegions();
    }

    refreshChangelogStatus = async () => {
        try {
            const status = await getChangelogStatus();
            if (status) {
                this.setState({ changelogNeedsAttention: !status.hasVisibleEntries });
            }
        } catch (error) {
            console.error('Could not load changelog status.', error);
        }
    };

    refreshFilterRegions = async () => {
        this.setState({ filterRegionsLoading: true, filterRegionsError: '' });
        try {
            const regions = await getRegions();
            if (!Array.isArray(regions)) {
                throw new Error('Regions could not be loaded.');
            }
            this.handleRegionsChange(regions);
        } catch (error) {
            this.setState({
                filterRegions: [],
                filterRegionsError: error?.message || 'Regions could not be loaded.',
            });
        } finally {
            this.setState({ filterRegionsLoading: false });
        }
    };

    handleRegionsChange = (regions) => {
        const filterRegions = [...(regions || [])].sort((left, right) => (
            Number(left.level ?? left.Level ?? 0) - Number(right.level ?? right.Level ?? 0)
            || String(left.name ?? left.Name ?? '').localeCompare(
                String(right.name ?? right.Name ?? ''),
                'en',
            )
        ));
        this.setState({ filterRegions, filterRegionsError: '' });
        const filter = parseAdminContentFilter(this.props.router?.location?.search || '');
        if (
            filter.regionId
            && !filterRegions.some(region => String(region.id ?? region.Id) === filter.regionId)
        ) {
            this.updateFilter({ regionId: '' });
        }
    };

    updateFilter = (patch) => {
        const location = this.props.router?.location;
        const search = updateAdminContentFilterSearch(location?.search || '', patch);
        this.props.router?.navigate(
            { pathname: location?.pathname || '/admin/overview', search },
            { replace: true },
        );
    };

    clearFilter = () => {
        const location = this.props.router?.location;
        const search = clearAdminContentFilterSearch(location?.search || '');
        this.props.router?.navigate(
            { pathname: location?.pathname || '/admin/overview', search },
            { replace: true },
        );
    };

    setSidebarCollapsed = (sidebarCollapsed) => {
        this.setState({ sidebarCollapsed });
        saveSidebarCollapsed(sidebarCollapsed);
    };

    toggleSidebar = () => {
        this.setSidebarCollapsed(!this.state.sidebarCollapsed);
    };

    handleNavigation = () => {
        if (isNarrowAdminViewport()) {
            this.setSidebarCollapsed(true);
        }
    };

    render() {
        const basePath = '/admin';
        const filter = parseAdminContentFilter(
            this.props.router?.location?.search || '',
        );
        const filterSearch = adminContentFilterSearch(filter);
        const adminPath = path => ({
            pathname: `${basePath}/${path}`,
            search: filterSearch,
        });
        const selectedRegion = this.state.filterRegions.find(region => (
            String(region.id ?? region.Id) === filter.regionId
        ));
        const selectedRegionName = selectedRegion
            ? String(selectedRegion.name ?? selectedRegion.Name)
            : '';
        const levelSummary = filter.minLevel && filter.maxLevel
            ? `levels ${filter.minLevel}–${filter.maxLevel}`
            : filter.minLevel
                ? `level ${filter.minLevel} and above`
                : filter.maxLevel
                    ? `up to level ${filter.maxLevel}`
                    : '';
        const filterSummary = [selectedRegionName, levelSummary]
            .filter(Boolean)
            .join(' · ') || 'All regions and levels';

        return (
            <div className={`admin_home${this.state.sidebarCollapsed ? ' is-sidebar-collapsed' : ''}`}>
                <ToastHost />
                <aside className="admin-sidebar">
                    <header className="admin-sidebar__header">
                        <strong hidden={this.state.sidebarCollapsed}>Admin</strong>
                        <button
                            aria-controls="admin-sidebar-content"
                            aria-expanded={!this.state.sidebarCollapsed}
                            aria-label={this.state.sidebarCollapsed
                                ? 'Expand admin navigation'
                                : 'Collapse admin navigation'}
                            className="admin-sidebar__toggle"
                            onClick={this.toggleSidebar}
                            type="button"
                        >
                            <span aria-hidden="true">
                                {this.state.sidebarCollapsed ? '›' : '‹'}
                            </span>
                        </button>
                    </header>
                    <div
                        className="admin-sidebar__content"
                        hidden={this.state.sidebarCollapsed}
                        id="admin-sidebar-content"
                    >
                        <nav aria-label="Admin sections" className="admin_home_navigation">
                            {ADMIN_NAV_GROUPS.map(group => (
                                <section className="admin-navigation-group" key={group.label}>
                                    <h2>{group.label}</h2>
                                    {group.items.map(([label, path]) => (
                                        <NavLink
                                            aria-label={path === 'changelog' && this.state.changelogNeedsAttention
                                                ? 'Changelog — no entries are active for the current version'
                                                : undefined}
                                            className={adminNavClassName}
                                            end
                                            key={path}
                                            onClick={this.handleNavigation}
                                            to={adminPath(path)}
                                        >
                                            <span>{label}</span>
                                            {path === 'changelog' && this.state.changelogNeedsAttention && (
                                                <span className="admin-changelog__warning" aria-hidden="true">!</span>
                                            )}
                                        </NavLink>
                                    ))}
                                </section>
                            ))}
                        </nav>
                        <section className="admin-global-filter" aria-label="Global content filter">
                            <h2>Content filter</h2>
                            <label>
                                Region
                                <select
                                    disabled={this.state.filterRegionsLoading || Boolean(this.state.filterRegionsError)}
                                    onChange={event => this.updateFilter({ regionId: event.target.value })}
                                    value={filter.regionId}
                                >
                                    <option value="">All regions</option>
                                    {this.state.filterRegions.map(region => (
                                        <option key={region.id ?? region.Id} value={region.id ?? region.Id}>
                                            {region.name ?? region.Name} · Level {region.level ?? region.Level}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <div className="admin-global-filter__levels">
                                <label>
                                    Min level
                                    <input
                                        min="1"
                                        onChange={event => this.updateFilter({ minLevel: event.target.value })}
                                        placeholder="Any"
                                        step="1"
                                        type="number"
                                        value={filter.minLevel || ''}
                                    />
                                </label>
                                <label>
                                    Max level
                                    <input
                                        min="1"
                                        onChange={event => this.updateFilter({ maxLevel: event.target.value })}
                                        placeholder="Any"
                                        step="1"
                                        type="number"
                                        value={filter.maxLevel || ''}
                                    />
                                </label>
                            </div>
                            <p className="admin-global-filter__summary" aria-live="polite">
                                {filterSummary}
                            </p>
                            <button
                                disabled={!filter.active}
                                onClick={this.clearFilter}
                                type="button"
                            >
                                Clear filters
                            </button>
                            {this.state.filterRegionsError ? (
                                <p className="admin-global-filter__error" role="alert">
                                    {this.state.filterRegionsError}{' '}
                                    <button onClick={this.refreshFilterRegions} type="button">Retry</button>
                                </p>
                            ) : null}
                        </section>
                    </div>
                </aside>
                <div className='admin_home_content'>
                    <Routes>
                        <Route
                            index
                            element={(
                                <Navigate
                                    replace
                                    to={{ pathname: `${basePath}/overview`, search: filterSearch }}
                                />
                            )}
                        />
                        <Route path="overview" element={<ADMIN_CONTENT_OVERVIEW filter={filter} />} />
                        <Route path="enemies" element={<ADMIN_ENEMY filter={filter} />} />
                        <Route
                            path="regions"
                            element={(
                                <ADMIN_REGIONS
                                    filter={filter}
                                    onRegionsChange={this.handleRegionsChange}
                                />
                            )}
                        />
                        <Route path="rarities" element={<ADMIN_RARITY />} />
                        <Route path="DamageTypes" element={<ADMIN_DAMAGATYPES />} />
                        <Route path="EnemyTypes" element={<ADMIN_ENEMYTYPES />} />
                        <Route path="ItemTypes" element={<ADMIN_ITEMTYPES />} />
                        <Route path="Items" element={<ADMIN_ITEMS filter={filter} />} />
                        <Route
                            path="professions"
                            element={<ADMIN_PROFESSIONS filter={filter} filterSearch={filterSearch} />}
                        />
                        <Route path="camp-buildings" element={<ADMIN_BASE_CAMP_BUILDINGS />} />
                        <Route path="starter-kits" element={<ADMIN_STARTER_KITS />} />
                        <Route path="Stats" element={<ADMIN_STATS />} />
                        <Route
                            path="Level"
                            element={(
                                <ADMIN_LEVEL
                                    filter={filter}
                                    regions={this.state.filterRegions}
                                />
                            )}
                        />
                        <Route path="Settings" element={<ADMIN_SETTINGS />} />
                        <Route path="tiles" element={<ADMIN_TILES />} />
                        <Route path="world-editor" element={<ADMIN_WORLD_EDITOR />} />
                        <Route path="loot-containers" element={<ADMIN_LOOT_CONTAINERS />} />
                        <Route path="operatives" element={<ADMIN_OPERATIVES />} />
                        <Route path="users" element={<ADMIN_USERS />} />
                        <Route path="content-promotion" element={<ADMIN_CONTENT_PROMOTION />} />
                        <Route path="wiki" element={<ADMIN_WIKI />} />
                        <Route
                            path="changelog"
                            element={<ADMIN_CHANGELOG onStatusChange={this.refreshChangelogStatus} />}
                        />

                    </Routes>
                </div>
            </div>
        );
    }
}

export default withRouter(AdminHome);
